/**
 * Product Form Page
 * Formulario de cadastro/edicao de produto (Design System)
 *
 * Anatomia (mockup databases-product-form.html):
 * - Header sticky com breadcrumb (nunca seta-voltar) + Cancelar (secundário) + Salvar (primário roxo)
 * - Seções em cards via FormSection/FormField shared (seguem o tema da página)
 * - Variações: linhas read-only + edição/criação SEMPRE por botão → modal (tema claro fixo, regra 9 / DS 1.9)
 * - Estoque: com variações, quantidade vira nota "Somado das variações"
 *
 * Funções (page real preservadas):
 * - Upload de fotos (drag/drop, múltiplo, remover) produto + variação
 * - Preço direto via MoneyInput → convertido para BusinessOpportunity no save
 * - Categorias de database.settings com fallback
 * - Validação: nome obrigatório, preço obrigatório (> 0)
 * - Tags (add por Enter/botão, dedup, remover)
 * - Modo new-vs-edit, estados loading/erro/dirty
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDatabase } from '../hooks/useDatabases';
import { useCreateDatabaseDocument, useDatabaseDocument, useNextRegistrationCode, useUpdateDatabaseDocument } from '../hooks/useDatabaseDocuments';
import { PhotoUploader } from '../components/PhotoUploader';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Textarea } from '@/shared/components/ui/Textarea';
import { MoneyInput } from '@/shared/components/ui/MoneyInput';
import { FormSection } from '@/shared/components/ui/FormSection';
import { FormField } from '@/shared/components/ui/FormField';
import { Breadcrumb } from '@/shared/components/ui/Breadcrumb';
import { Button } from '@/shared/components/ui/Button';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { extractApiFieldErrors } from '@/shared/utils/apiFieldErrors';
import type {
  DatabaseProductData,
  ProductCategory,
  ProductItemStatus,
  BusinessOpportunity,
  BusinessOpportunityType,
  OpportunityStatus,
  DatabaseType,
} from '@troia-v3/types';
import { Icon, type IconName } from '@/shared/icons';

interface ProductFormPageProps {
  documentId?: string;
}

// ── Option lists ──

const defaultCategoryOptions = [
  { value: 'electronics', label: 'Eletrônicos' },
  { value: 'furniture', label: 'Móveis' },
  { value: 'clothing', label: 'Vestuário' },
  { value: 'food', label: 'Alimentos' },
  { value: 'books', label: 'Livros' },
  { value: 'toys', label: 'Brinquedos' },
  { value: 'sports', label: 'Esportes' },
  { value: 'beauty', label: 'Beleza' },
  { value: 'automotive', label: 'Automotivo' },
  { value: 'home_garden', label: 'Casa e Jardim' },
  { value: 'other', label: 'Outros' },
];

const statusOptions = [
  { value: 'active', label: 'Ativo' },
  { value: 'inactive', label: 'Inativo' },
  { value: 'outofstock', label: 'Sem estoque' },
  { value: 'discontinued', label: 'Descontinuado' },
];

const stockUnitOptions = [
  { value: 'unit', label: 'Unidade' },
  { value: 'kg', label: 'Quilograma (kg)' },
  { value: 'liter', label: 'Litro (L)' },
  { value: 'meter', label: 'Metro (m)' },
  { value: 'box', label: 'Caixa' },
  { value: 'pack', label: 'Pacote' },
];

const formatBRL = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

// ── Form types ──

interface VariationFormData {
  id: string;
  name: string;
  sku: string;
  stock: string;
  price: number | undefined;
  photos: string[];
}

interface ProductFormState {
  name: string;
  description: string;
  sku: string;
  category: string;
  brand: string;
  price: number | undefined;
  stockUnit: string;
  stock: string;
  itemStatus: string;
  photos: string[];
  /** Liga quando a PESSOA reordena as fotos — ver media.photosManualOrder. */
  photosManualOrder: boolean;
  variations: VariationFormData[];
  tags: string[];
}

const defaultForm: ProductFormState = {
  name: '',
  description: '',
  sku: '',
  category: 'other',
  brand: '',
  price: undefined,
  stockUnit: 'unit',
  stock: '0',
  itemStatus: 'active',
  photos: [],
  photosManualOrder: false,
  variations: [],
  tags: [],
};

const emptyVariation = (): VariationFormData => ({
  id: crypto.randomUUID(),
  name: '',
  sku: '',
  stock: '0',
  price: undefined,
  photos: [],
});

// ── Variation read-only chip ──

const VarChip: React.FC<{ icon?: IconName; mono?: boolean; muted?: boolean; children: React.ReactNode }> = ({
  icon,
  mono = false,
  muted = false,
  children,
}) => (
  <span
    className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-full text-xs"
    style={{
      backgroundColor: 'var(--theme-page-background)',
      border: '1px solid var(--theme-card-border)',
      color: muted ? 'var(--theme-muted-text)' : 'var(--theme-card-text)',
      fontFamily: mono ? 'ui-monospace, Menlo, monospace' : undefined,
    }}
  >
    {icon && <Icon name={icon} className="w-3 h-3" />}
    {children}
  </span>
);

// ── Main Component ──

export const ProductFormPage: React.FC<ProductFormPageProps> = ({ documentId }) => {
  const { id: databaseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!documentId;

  const { data: database } = useDatabase(databaseId);
  const { data: existingDocument, isLoading: isLoadingDocument } = useDatabaseDocument<DatabaseProductData>(
    documentId || '',
    isEditing
  );
  const createMutation = useCreateDatabaseDocument();
  const updateMutation = useUpdateDatabaseDocument<DatabaseProductData>();

  const [form, setForm] = useState<ProductFormState>(defaultForm);
  const [formLoaded, setFormLoaded] = useState(false);
  // Enquanto a pessoa não digitar o SKU, a sugestão preenche sozinha.
  const [skuTocado, setSkuTocado] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(!isEditing);

  // Sugestão do próximo SKU — só em cadastro NOVO e em base não sincronizada.
  const { data: proximoCodigo, refetch: recarregarCodigo } = useNextRegistrationCode(databaseId, { enabled: !isEditing });

  useEffect(() => {
    if (isEditing || skuTocado) return;
    const sugestao = proximoCodigo?.sugestao;
    if (!sugestao || sugestao === form.sku) return;
    setForm((prev) => ({ ...prev, sku: sugestao }));
  }, [isEditing, skuTocado, proximoCodigo?.sugestao, form.sku]);

  // Variation modal state (edição sempre por modal — DS 1.9)
  const [variationModalOpen, setVariationModalOpen] = useState(false);
  const [variationDraft, setVariationDraft] = useState<VariationFormData | null>(null);
  const [variationEditIndex, setVariationEditIndex] = useState<number | null>(null);

  // Populate form with existing data when editing
  useEffect(() => {
    if (isEditing && existingDocument && !formLoaded) {
      const product = existingDocument.data;
      const mainOpportunity = product.opportunities?.find(o => o.type === 'sale');
      setForm({
        name: product.name || '',
        description: product.description || '',
        sku: product.sku || '',
        category: product.category || 'other',
        brand: product.brand || '',
        price: mainOpportunity?.pricing?.amount,
        stockUnit: product.inventory?.stockUnit || 'unit',
        stock: product.inventory?.stock?.toString() || '0',
        itemStatus: product.itemStatus || 'active',
        photos: product.media?.photos || [],
        photosManualOrder: product.media?.photosManualOrder === true,
        variations: product.variations?.map(v => ({
          id: v.id,
          name: v.name,
          sku: v.sku || '',
          stock: v.stock?.toString() || '0',
          price: v.opportunities?.find(o => o.type === 'sale')?.pricing?.amount,
          photos: v.media?.photos || [],
        })) || [],
        tags: product.tags || [],
      });
      setFormLoaded(true);
      setHasChanges(false);
    }
  }, [isEditing, existingDocument, formLoaded]);

  // Categories: from database settings or defaults
  const categoryOptions = (database?.settings as Record<string, unknown> | undefined)?.categories
    ? ((database!.settings as Record<string, unknown>).categories as string[]).map(c => ({ value: c, label: c }))
    : defaultCategoryOptions;

  const handleBack = () => {
    if (isEditing && documentId) {
      navigate(`/databases/${databaseId}/documents/${documentId}`);
    } else {
      navigate(`/databases/${databaseId}`);
    }
  };

  if (isEditing && isLoadingDocument) {
    return (
      <div className="min-h-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <div className="flex flex-col items-center gap-4">
          <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: 'var(--theme-primary)' }} />
          <span style={{ color: 'var(--theme-muted-text)' }}>Carregando produto...</span>
        </div>
      </div>
    );
  }

  const updateField = <K extends keyof ProductFormState>(field: K, value: ProductFormState[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // ── Variations (modal-based) ──
  const openNewVariation = () => {
    setVariationDraft(emptyVariation());
    setVariationEditIndex(null);
    setVariationModalOpen(true);
  };

  const openEditVariation = (index: number) => {
    const variation = form.variations[index];
    if (!variation) return;
    setVariationDraft({ ...variation });
    setVariationEditIndex(index);
    setVariationModalOpen(true);
  };

  const closeVariationModal = () => {
    setVariationModalOpen(false);
    setVariationDraft(null);
    setVariationEditIndex(null);
  };

  const updateDraft = <K extends keyof VariationFormData>(field: K, value: VariationFormData[K]) => {
    setVariationDraft(prev => (prev ? { ...prev, [field]: value } : prev));
  };

  const saveVariation = () => {
    if (!variationDraft) return;
    if (variationEditIndex === null) {
      updateField('variations', [...form.variations, variationDraft]);
    } else {
      const updated = [...form.variations];
      updated[variationEditIndex] = variationDraft;
      updateField('variations', updated);
    }
    closeVariationModal();
  };

  const removeVariation = (index: number) => {
    updateField('variations', form.variations.filter((_, i) => i !== index));
  };

  const variationsStockSum = form.variations.reduce((sum, v) => sum + (Number(v.stock) || 0), 0);

  // ── Tags ──
  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      updateField('tags', [...form.tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    updateField('tags', form.tags.filter(t => t !== tag));
  };

  // ── Price → BusinessOpportunity conversion ──
  const priceToOpportunity = (price: number): BusinessOpportunity => {
    const now = new Date().toISOString();
    return {
      id: crypto.randomUUID(),
      type: 'sale' as BusinessOpportunityType,
      status: 'active' as OpportunityStatus,
      pricing: { amount: price, currency: 'BRL' },
      createdAt: now,
      updatedAt: now,
    };
  };

  // ── Save ──
  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Nome é obrigatório');
      return;
    }
    if (form.price === undefined || form.price <= 0) {
      setError('Preço é obrigatório');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const productData: DatabaseProductData = {
        name: form.name.trim(),
        description: form.description.trim(),
        sku: form.sku.trim() || undefined,
        category: form.category as ProductCategory,
        brand: form.brand.trim() || undefined,
        inventory: {
          trackInventory: true,
          stockUnit: form.stockUnit as DatabaseProductData['inventory']['stockUnit'],
          stock: form.variations.length === 0 ? Number(form.stock) || 0 : undefined,
        },
        opportunities: [priceToOpportunity(form.price)],
        variations: form.variations.length > 0
          ? form.variations.map(v => ({
              id: v.id,
              name: v.name,
              attributes: {},
              sku: v.sku || undefined,
              stock: Number(v.stock) || 0,
              media: v.photos.length > 0 ? { photos: v.photos } : undefined,
              opportunities: v.price ? [priceToOpportunity(v.price)] : undefined,
            }))
          : undefined,
        itemStatus: form.itemStatus as ProductItemStatus,
        media: form.photos.length > 0
          ? { photos: form.photos, ...(form.photosManualOrder ? { photosManualOrder: true } : {}) }
          : undefined,
        tags: form.tags.length > 0 ? form.tags : undefined,
      };

      if (isEditing && documentId) {
        await updateMutation.mutateAsync({
          documentId,
          data: productData,
        });
        navigate(`/databases/${databaseId}/documents/${documentId}`);
      } else {
        await createMutation.mutateAsync({
          databaseId: databaseId!,
          type: 'products' as DatabaseType,
          data: productData,
          metadata: { source: 'manual' },
        });
        navigate(`/databases/${databaseId}`);
      }
    } catch (err: unknown) {
      const parsed = extractApiFieldErrors(err);
      setError(parsed.message);
      // SKU recusado: recarrega a sugestão para os atalhos não oferecerem de
      // novo o código que acabou de ser tomado.
      if (parsed.fieldErrors['sku']) void recarregarCodigo();
    } finally {
      setSaving(false);
    }
  };

  const currentCrumb = isEditing ? (form.name || 'Editar produto') : 'Novo produto';
  const saveHint = isEditing ? 'Alterações não salvas' : 'Preencha os campos obrigatórios';

  return (
    <div className="min-h-full" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Sticky Header */}
      <div
        className="sticky top-0 z-10"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderBottom: '1px solid var(--theme-card-border)',
        }}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Breadcrumb shared (F25-31; nunca seta-voltar) */}
            <Breadcrumb
              items={[
                { label: 'Bases de dados', onNavigate: () => navigate('/databases'), testid: 'product-form-crumb-databases' },
                { label: database?.name || 'Base', onNavigate: () => navigate(`/databases/${databaseId}`), testid: 'product-form-crumb-database' },
                { label: currentCrumb, testid: 'product-form-crumb-current' },
              ]}
              mobileLegend={`Bases de dados · ${database?.name || 'Base'}`}
              data-testid="product-form-breadcrumb"
            />

            <div className="flex items-center gap-3 flex-shrink-0">
              {hasChanges && (
                <span className="hidden md:inline text-sm font-medium" style={{ color: 'var(--theme-warning-color)' }}>
                  {saveHint}
                </span>
              )}
              <Button
                variant="secondary"
                data-testid="product-form-cancel"
                onClick={handleBack}
                disabled={saving}
                className="hidden sm:inline-flex"
              >
                Cancelar
              </Button>
              <Button
                data-testid="product-form-save"
                icon="save"
                loading={saving}
                disabled={saving || (isEditing && !hasChanges)}
                onClick={handleSave}
              >
                {saving
                  ? (isEditing ? 'Salvando...' : 'Criando...')
                  : (isEditing ? 'Salvar' : 'Criar produto')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8 space-y-6">
        {error && (
          <div
            data-testid="product-form-error"
            className="p-4 rounded-lg text-sm"
            style={{
              backgroundColor: 'rgba(239,68,68,0.1)',
              color: 'var(--theme-error-color)',
              border: '1px solid var(--theme-error-color)',
            }}
          >
            {error}
          </div>
        )}

        {/* Fotos do Produto */}
        <FormSection
          title="Fotos do Produto"
          icon="image"
          description="A primeira foto é a principal. Arraste arquivos ou clique para enviar"
        >
          <PhotoUploader
            photos={form.photos}
            folder="products"
            idPrefix="product"
            onChange={(photos, opcoes) => {
              setForm((prev) => ({
                ...prev,
                photos,
                photosManualOrder: prev.photosManualOrder || opcoes?.reordenadoAMao === true,
              }));
            }}
          />
        </FormSection>

        {/* Informacoes Basicas */}
        <FormSection title="Informações Básicas" icon="package" description="Identificação do produto no catálogo">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Nome do produto" required>
              <Input
                data-testid="product-form-name"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Ex: Camiseta Básica"
              />
            </FormField>

            <FormField label="Categoria">
              <Select
                data-testid="product-form-category"
                value={form.category}
                onChange={(value) => updateField('category', value)}
                options={categoryOptions}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Status">
              <Select
                data-testid="product-form-status"
                value={form.itemStatus}
                onChange={(value) => updateField('itemStatus', value)}
                options={statusOptions}
              />
            </FormField>

            <FormField
              label="SKU"
              description={!isEditing && proximoCodigo?.sugestao ? 'Sugerido pelo sistema — pode alterar' : undefined}
            >
              <Input
                data-testid="product-form-sku"
                value={form.sku}
                onChange={(e) => {
                  setSkuTocado(true);
                  updateField('sku', e.target.value);
                }}
                placeholder="Código do produto"
              />
              {!isEditing && (proximoCodigo?.prefixos.length ?? 0) > 1 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {proximoCodigo?.prefixos.map((p) => (
                    <button
                      key={p.prefixo}
                      type="button"
                      onClick={() => {
                        setSkuTocado(true);
                        updateField('sku', p.proximo);
                      }}
                      className="px-2 py-1 rounded-md text-xs font-medium border transition-colors"
                      style={{
                        borderColor: form.sku === p.proximo ? 'var(--theme-primary)' : 'var(--theme-card-border)',
                        color: form.sku === p.proximo ? 'var(--theme-primary)' : 'var(--theme-muted-text)',
                        backgroundColor: 'transparent',
                      }}
                      data-testid={`product-sku-prefix-${p.prefixo}`}
                    >
                      {p.proximo}
                    </button>
                  ))}
                </div>
              )}
            </FormField>

            <FormField label="Marca">
              <Input
                data-testid="product-form-brand"
                value={form.brand}
                onChange={(e) => updateField('brand', e.target.value)}
                placeholder="Ex: Nike, Apple..."
              />
            </FormField>
          </div>

          <FormField label="Descrição">
            <Textarea
              data-testid="product-form-description"
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={4}
              placeholder="Descreva o produto..."
            />
          </FormField>
        </FormSection>

        {/* Preco */}
        <FormSection title="Preço" icon="dollar-sign" description="Preço de venda do produto">
          <div className="max-w-xs">
            <FormField label="Preço de venda" required>
              <MoneyInput
                data-testid="product-form-price"
                value={form.price}
                onChange={(value) => updateField('price', value)}
                placeholder="0,00"
                hideHelperText
              />
            </FormField>
          </div>
        </FormSection>

        {/* Estoque */}
        <FormSection
          title="Estoque"
          icon="box"
          description={
            form.variations.length > 0
              ? 'Com variações, o estoque é controlado por variação'
              : 'Unidade e quantidade disponível'
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
            <FormField label="Unidade de medida">
              <Select
                data-testid="product-form-stock-unit"
                value={form.stockUnit}
                onChange={(value) => updateField('stockUnit', value)}
                options={stockUnitOptions}
              />
            </FormField>

            {form.variations.length > 0 ? (
              <FormField label="Quantidade">
                <div className="flex items-center gap-2 h-[34px]" style={{ color: 'var(--theme-muted-text)' }}>
                  <Icon name="info" className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">
                    Somado das variações:{' '}
                    <b style={{ color: 'var(--theme-card-text)' }}>{variationsStockSum}</b>
                  </span>
                </div>
              </FormField>
            ) : (
              <FormField label="Quantidade em estoque">
                <Input
                  data-testid="product-form-stock"
                  type="number"
                  value={form.stock}
                  onChange={(e) => updateField('stock', e.target.value)}
                  placeholder="0"
                />
              </FormField>
            )}
          </div>
        </FormSection>

        {/* Variacoes — linhas read-only, edição por modal (DS 1.9) */}
        <FormSection
          title="Variações"
          icon="tag"
          description="Tamanhos, cores ou modelos com estoque e preço próprios"
        >
          {form.variations.map((variation, index) => (
            <div
              key={variation.id}
              data-testid={`product-form-variation-${index}`}
              className="flex items-start gap-3 p-3 rounded-lg"
              style={{ border: '1px solid var(--theme-card-border)' }}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
                  {variation.name || 'Sem nome'}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {variation.sku && <VarChip mono>{variation.sku}</VarChip>}
                  <VarChip icon="box">{Number(variation.stock) || 0} un</VarChip>
                  {variation.price !== undefined ? (
                    <VarChip icon="dollar-sign">{formatBRL(variation.price)}</VarChip>
                  ) : (
                    <VarChip icon="dollar-sign" muted>Herda do produto</VarChip>
                  )}
                  {variation.photos.length > 0 ? (
                    <VarChip icon="image">
                      {variation.photos.length} foto{variation.photos.length > 1 ? 's' : ''}
                    </VarChip>
                  ) : (
                    <VarChip icon="image" muted>sem fotos</VarChip>
                  )}
                </div>
              </div>
              <button
                type="button"
                data-testid={`product-form-variation-edit-${index}`}
                onClick={() => openEditVariation(index)}
                title="Editar variação"
                className="p-1.5 rounded transition-colors self-center"
                style={{ color: 'var(--theme-muted-text)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-primary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
              >
                <Icon name="edit" className="w-4 h-4" />
              </button>
              <button
                type="button"
                data-testid={`product-form-variation-delete-${index}`}
                onClick={() => removeVariation(index)}
                title="Excluir variação"
                className="p-1.5 rounded transition-colors self-center"
                style={{ color: 'var(--theme-muted-text)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-error-color)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
              >
                <Icon name="trash" className="w-4 h-4" />
              </button>
            </div>
          ))}

          <button
            type="button"
            data-testid="product-form-add-variation"
            onClick={openNewVariation}
            className="inline-flex items-center justify-center gap-2 w-full h-10 rounded-lg text-sm font-medium transition-colors"
            style={{
              color: 'var(--theme-muted-text)',
              border: '1px dashed var(--theme-card-border)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <Icon name="plus" className="w-4 h-4" />
            Adicionar variação
          </button>
        </FormSection>

        {/* Tags */}
        <FormSection title="Tags" icon="tag" description="Ajudam a busca do agente de IA e os filtros">
          <div className="flex items-center gap-2">
            <Input
              data-testid="product-form-tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="Nova tag (Enter adiciona)"
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); addTag(); }
              }}
            />
            <Button
              variant="secondary"
              icon="plus"
              data-testid="product-form-add-tag"
              onClick={addTag}
              className="flex-shrink-0"
            >
              Adicionar
            </Button>
          </div>

          {form.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {form.tags.map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: 'rgba(139,92,246,0.1)', color: 'var(--theme-primary)' }}
                >
                  {tag}
                  <button
                    type="button"
                    data-testid={`product-form-tag-remove-${tag}`}
                    onClick={() => removeTag(tag)}
                    className="hover:opacity-70 transition-opacity"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </FormSection>
      </div>

      {/* Modal: nova/editar variação (tema claro fixo — DS 1.9 / regra 9) */}
      <Modal isOpen={variationModalOpen} onClose={closeVariationModal} maxWidth="xl" className="max-h-[88vh] flex flex-col">
        <ModalHeader onClose={closeVariationModal}>
          <Icon name={variationEditIndex === null ? 'plus' : 'edit'} className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} />
          <h2 className="text-base font-bold" style={{ color: 'var(--theme-card-text)' }}>
            {variationEditIndex === null ? 'Nova variação' : 'Editar variação'}
          </h2>
        </ModalHeader>
        <ModalContent className="flex-1 min-h-0 overflow-y-auto">
          {variationDraft && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Nome" required>
                  <Input
                    data-testid="variation-modal-name"
                    value={variationDraft.name}
                    onChange={(e) => updateDraft('name', e.target.value)}
                    placeholder="Ex: P / Azul"
                  />
                </FormField>
                <FormField label="SKU">
                  <Input
                    data-testid="variation-modal-sku"
                    value={variationDraft.sku}
                    onChange={(e) => updateDraft('sku', e.target.value)}
                    placeholder="SKU da variação"
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Estoque" required>
                  <Input
                    data-testid="variation-modal-stock"
                    type="number"
                    value={variationDraft.stock}
                    onChange={(e) => updateDraft('stock', e.target.value)}
                    placeholder="0"
                  />
                </FormField>
                <FormField label="Preço">
                  <MoneyInput
                    data-testid="variation-modal-price"
                    value={variationDraft.price}
                    onChange={(value) => updateDraft('price', value)}
                    placeholder="Herda do produto"
                    hideHelperText
                  />
                </FormField>
              </div>

              <FormField label="Fotos da variação">
                <PhotoUploader
                  idPrefix="variation"
                  folder="products"
                  photos={variationDraft.photos}
                  onChange={(photos) => updateDraft('photos', photos)}
                  compact
                />
              </FormField>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" data-testid="variation-modal-cancel" onClick={closeVariationModal}>
            Cancelar
          </Button>
          <Button
            icon="check"
            data-testid="variation-modal-save"
            onClick={saveVariation}
            disabled={!variationDraft?.name.trim()}
          >
            {variationEditIndex === null ? 'Adicionar variação' : 'Salvar'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
